/**
 * Logger.ts - Configurable logging utility for the chess application
 * 
 * This module provides a simple logging interface built on top of the 'loglevel' library.
 * By default, logs are set to WARN level, meaning only warnings and errors will be displayed.
 * To enable more verbose logging during development or debugging, use the exported helper
 * functions like enableDebugLogs() or enableInfoLogs().
 * 
 * Usage examples:
 * 
 * 1. Basic logging:
 *    import logger from './utils/Logger';
 *    logger.debug('Debug message');
 *    logger.info('Info message');
 *    logger.warn('Warning message');
 *    logger.error('Error message');
 * 
 * 2. Enabling different log levels:
 *    import { enableDebugLogs } from './utils/Logger';
 *    enableDebugLogs(); // Now debug, info, warn, and error logs will be shown
 *    
 *    // Or in another component:
 *    import { enableInfoLogs } from './utils/Logger';
 *    enableInfoLogs(); // Now only info, warn, and error logs will be shown
 * 
 * Log levels hierarchy (from most to least verbose):
 * - DEBUG: All logs shown
 * - INFO: Info, warnings and errors shown
 * - WARN: Only warnings and errors shown
 * - ERROR: Only errors shown
 * - SILENT: No logs shown
 */

import log from 'loglevel';

// Set both the default level AND the current level to WARN
// This ensures any new logger instances use WARN as default,
// and that the current logger is immediately set to WARN
log.setDefaultLevel(log.levels.WARN);
log.setLevel(log.levels.WARN); // This explicitly sets the current level

/**
 * Enable all logs (DEBUG and above)
 */
export const enableDebugLogs = () => log.setLevel(log.levels.DEBUG);

/**
 * Enable INFO logs and above (INFO, WARN, ERROR)
 */
export const enableInfoLogs = () => log.setLevel(log.levels.INFO);

/**
 * Enable WARN logs and above (WARN, ERROR)
 */
export const enableWarnLogs = () => log.setLevel(log.levels.WARN);

/**
 * Enable only ERROR logs
 */
export const enableErrorLogs = () => log.setLevel(log.levels.ERROR);

/**
 * Disable all logs completely
 */
export const disableLogs = () => log.setLevel(log.levels.SILENT);

// Export the logger instance
export default log;